import React, { useEffect, useMemo, useState } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { listOrganizations, listRestRules, listSeasons, listUsers as listAppUsers } from "../graphql/queries";
import { createLeague, deleteLeague, updateLeague } from "../graphql/mutations";
import { formatApiError } from "../lib/formatApiError";
import AppDialog from "../components/AppDialog";
import { buildAdminScope } from "../lib/adminScope";

interface League {
  id: string;
  name: string;
  organizationId: string;
  restRuleId?: string | null;
  seasonId?: string | null;
  admins?: string[] | null;
}

interface Organization {
  id: string;
  name: string;
  restRuleId?: string | null;
}

interface RestRule {
  id: string;
  name: string;
  global?: boolean | null;
  organizationId?: string | null;
}

interface Season {
  id: string;
  name: string;
  year: number;
}

interface AppUser {
  id: string;
  email?: string | null;
  givenName?: string | null;
  familyName?: string | null;
  roles?: string[] | null;
}

interface LeaguesPageProps {
  userRoles: string[];
}

const LeaguesPage: React.FC<LeaguesPageProps> = ({ userRoles }) => {
  const listLeaguesWithSeason = /* GraphQL */ `
    query ListLeaguesWithSeason($limit: Int, $nextToken: String) {
      listLeagues(limit: $limit, nextToken: $nextToken) {
        items {
          id
          name
          organizationId
          restRuleId
          seasonId
          admins
        }
        nextToken
      }
    }
  `;

  const [leagues, setLeagues] = useState<League[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [rules, setRules] = useState<RestRule[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [appUsers, setAppUsers] = useState<AppUser[]>([]);
  const [username, setUsername] = useState("");
  const [newName, setNewName] = useState("");
  const [newOrgId, setNewOrgId] = useState("");
  const [newSeasonId, setNewSeasonId] = useState("");
  const [newRestRuleId, setNewRestRuleId] = useState("");
  const [newAdmins, setNewAdmins] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingSeasonId, setEditingSeasonId] = useState("");
  const [editingRestRuleId, setEditingRestRuleId] = useState("");
  const [editingAdmins, setEditingAdmins] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [filterText, setFilterText] = useState("");
  const [filterOrgId, setFilterOrgId] = useState("");
  const [filterSeasonId, setFilterSeasonId] = useState("");
  const [filterHasRule, setFilterHasRule] = useState<"all" | "yes" | "no">("all");
  const [sortKey, setSortKey] = useState<"name" | "organization" | "season" | "rule">("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const adminScope = useMemo(
    () => buildAdminScope({ userRoles, username, organizations, leagues }),
    [leagues, organizations, userRoles, username]
  );
  const canManage = adminScope.isGlobalAdmin || adminScope.isScopedCommish;

  const orgById = useMemo(
    () => organizations.reduce<Record<string, string>>((acc, org) => ({ ...acc, [org.id]: org.name }), {}),
    [organizations]
  );
  const seasonLabelById = useMemo(
    () =>
      seasons.reduce<Record<string, string>>((acc, season) => {
        acc[season.id] = `${season.name} ${season.year}`;
        return acc;
      }, {}),
    [seasons]
  );

  const loadData = async (silent = false) => {
    try {
      if (!silent) {
        setErrorMessage("");
      }
      const authUser = await Auth.currentAuthenticatedUser();
      setUsername(authUser?.username ?? "");
      const [leagueRes, orgRes, ruleRes, seasonsRes, usersRes] = await Promise.all([
        API.graphql(graphqlOperation(listLeaguesWithSeason, { limit: 300 })),
        API.graphql(graphqlOperation(listOrganizations, { limit: 200 })),
        API.graphql(graphqlOperation(listRestRules, { limit: 300 })),
        API.graphql(graphqlOperation(listSeasons, { limit: 300 })),
        API.graphql(graphqlOperation(listAppUsers, { limit: 500 }))
      ]);

      const leagueItems = ((leagueRes as any)?.data?.listLeagues?.items ?? []).filter(Boolean);
      const orgItems = ((orgRes as any)?.data?.listOrganizations?.items ?? []).filter(Boolean);
      const ruleItems = ((ruleRes as any)?.data?.listRestRules?.items ?? []).filter(Boolean);
      const seasonItems = ((seasonsRes as any)?.data?.listSeasons?.items ?? []).filter(Boolean);
      const appUserItems = ((usersRes as any)?.data?.listUsers?.items ?? []).filter(Boolean);

      setLeagues(leagueItems);
      setOrganizations(orgItems);
      setRules(ruleItems);
      setSeasons(seasonItems);
      setAppUsers(appUserItems);
    } catch (error) {
      if (!silent) {
        setErrorMessage(`Failed to load leagues: ${formatApiError(error)}`);
      }
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newOrgId) {
      return;
    }
    if (!adminScope.canCreateLeagueInOrganization(newOrgId)) {
      setErrorMessage("Commish can only create leagues inside assigned organizations.");
      return;
    }

    try {
      setErrorMessage("");
      const result: any = await API.graphql(
        graphqlOperation(createLeague, {
          input: {
            name: newName.trim(),
            organizationId: newOrgId,
            seasonId: newSeasonId || null,
            restRuleId: newRestRuleId || null,
            admins: newAdmins
          }
        })
      );
      if (!result?.data?.createLeague) {
        throw new Error(formatApiError(result));
      }
      setNewName("");
      setNewOrgId("");
      setNewSeasonId("");
      setNewRestRuleId("");
      setNewAdmins([]);
      setCreateOpen(false);
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to create league: ${formatApiError(error)}`);
    }
  };

  const handleUpdate = async () => {
    if (!editingId || !editingName.trim()) {
      return;
    }
    const league = leagues.find((item) => item.id === editingId);
    if (!adminScope.canManageLeague(editingId, league?.organizationId)) {
      setErrorMessage("Commish can only edit assigned leagues.");
      return;
    }

    try {
      setErrorMessage("");
      const result: any = await API.graphql(
        graphqlOperation(updateLeague, {
          input: {
            id: editingId,
            name: editingName.trim(),
            seasonId: editingSeasonId || null,
            restRuleId: editingRestRuleId || null,
            admins: editingAdmins
          }
        })
      );
      if (!result?.data?.updateLeague) {
        throw new Error(formatApiError(result));
      }
      setEditingId(null);
      setEditingName("");
      setEditingSeasonId("");
      setEditingRestRuleId("");
      setEditingAdmins([]);
      setEditOpen(false);
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to update league: ${formatApiError(error)}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this league?")) {
      return;
    }
    const league = leagues.find((item) => item.id === id);
    if (!adminScope.canManageLeague(id, league?.organizationId)) {
      setErrorMessage("Commish can only delete assigned leagues.");
      return;
    }

    try {
      setErrorMessage("");
      const result: any = await API.graphql(graphqlOperation(deleteLeague, { input: { id } }));
      if (!result?.data?.deleteLeague) {
        throw new Error(formatApiError(result));
      }
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to delete league: ${formatApiError(error)}`);
    }
  };

  const rulesByOrg = useMemo(() => {
    const out: Record<string, RestRule[]> = {};
    for (const org of organizations) {
      out[org.id] = rules.filter((rule) => rule.global || rule.organizationId === org.id);
    }
    return out;
  }, [organizations, rules]);
  const ruleNameById = useMemo(
    () => rules.reduce<Record<string, string>>((acc, rule) => ({ ...acc, [rule.id]: rule.name }), {}),
    [rules]
  );
  const availableCommishes = useMemo(
    () =>
      appUsers
        .filter((user) => (user.roles ?? []).some((role) => ["SuperAdmin", "Commish"].includes(role)))
        .sort((a, b) => {
          const aLabel = `${a.givenName ?? ""} ${a.familyName ?? ""}`.trim() || a.email || a.id;
          const bLabel = `${b.givenName ?? ""} ${b.familyName ?? ""}`.trim() || b.email || b.id;
          return aLabel.localeCompare(bLabel);
        }),
    [appUsers]
  );
  const userLabelById = useMemo(
    () =>
      appUsers.reduce<Record<string, string>>((acc, user) => {
        acc[user.id] = `${user.givenName ?? ""} ${user.familyName ?? ""}`.trim() || user.email || user.id;
        return acc;
      }, {}),
    [appUsers]
  );
  const summarizeAdmins = (admins: string[] | null | undefined) => {
    const resolved = (admins ?? []).map((id) => userLabelById[id] ?? id).filter(Boolean);
    if (resolved.length === 0) {
      return "None";
    }
    if (resolved.length <= 2) {
      return resolved.join(", ");
    }
    return `${resolved.slice(0, 2).join(", ")} +${resolved.length - 2}`;
  };

  const visibleOrganizations = useMemo(
    () => organizations.filter((organization) => adminScope.isGlobalAdmin || adminScope.canManageOrganization(organization.id)),
    [adminScope, organizations]
  );
  const visibleLeagues = useMemo(
    () => leagues.filter((league) => adminScope.isGlobalAdmin || adminScope.canManageLeague(league.id, league.organizationId)),
    [adminScope, leagues]
  );

  const sortedLeagues = useMemo(() => {
    const normalizedText = filterText.trim().toLowerCase();
    const filtered = visibleLeagues.filter((league) => {
      const orgName = orgById[league.organizationId] ?? "";
      const hasRule = Boolean(league.restRuleId);
      const textHaystack = `${league.name} ${orgName}`.toLowerCase();
      if (normalizedText && !textHaystack.includes(normalizedText)) {
        return false;
      }
      if (filterOrgId && league.organizationId !== filterOrgId) {
        return false;
      }
      if (filterSeasonId && league.seasonId !== filterSeasonId) {
        return false;
      }
      if (filterHasRule === "yes" && !hasRule) {
        return false;
      }
      if (filterHasRule === "no" && hasRule) {
        return false;
      }
      return true;
    });
    return [...filtered].sort((a, b) => {
      const compare =
        sortKey === "organization"
          ? (orgById[a.organizationId] ?? "").localeCompare(orgById[b.organizationId] ?? "")
          : sortKey === "season"
            ? (seasonLabelById[a.seasonId ?? ""] ?? "").localeCompare(seasonLabelById[b.seasonId ?? ""] ?? "")
          : sortKey === "rule"
            ? (a.restRuleId ? 1 : 0) - (b.restRuleId ? 1 : 0)
            : a.name.localeCompare(b.name);
      return sortDirection === "asc" ? compare : -compare;
    });
  }, [filterHasRule, filterOrgId, filterSeasonId, filterText, orgById, seasonLabelById, sortDirection, sortKey, visibleLeagues]);

  const toggleSort = (key: "name" | "organization" | "season" | "rule") => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Leagues</h1>
      {errorMessage && <p className="mb-4" style={{ color: "#c1121f" }}>{errorMessage}</p>}

      {(adminScope.isGlobalAdmin || adminScope.managedOrganizationIds.size > 0) && (
        <div className="bubble-card p-4 mb-4 flex gap-2">
          <button onClick={() => setCreateOpen(true)} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            Add League
          </button>
        </div>
      )}

      <div className="bubble-card p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-2">
        <input
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          placeholder="Filter by league or organization"
          className="border px-3 py-2 rounded"
        />
        <select value={filterOrgId} onChange={(event) => setFilterOrgId(event.target.value)} className="border px-3 py-2 rounded">
          <option value="">All organizations</option>
          {visibleOrganizations.map((organization) => (
            <option key={organization.id} value={organization.id}>
              {organization.name}
            </option>
          ))}
        </select>
        <select value={filterSeasonId} onChange={(event) => setFilterSeasonId(event.target.value)} className="border px-3 py-2 rounded">
          <option value="">All seasons</option>
          {seasons.map((season) => (
            <option key={season.id} value={season.id}>
              {season.name} {season.year}
            </option>
          ))}
        </select>
        <select value={filterHasRule} onChange={(event) => setFilterHasRule(event.target.value as "all" | "yes" | "no")} className="border px-3 py-2 rounded">
          <option value="all">All rule states</option>
          <option value="yes">With assigned rule</option>
          <option value="no">Without assigned rule</option>
        </select>
      </div>

      <div className="bubble-table-wrapper">
        <table className="bubble-table">
          <thead>
            <tr>
              <th><button className="table-sort" onClick={() => toggleSort("name")}>League</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("organization")}>Organization</button></th>
              <th>Commishes</th>
              <th><button className="table-sort" onClick={() => toggleSort("season")}>Season</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("rule")}>Rule</button></th>
              {canManage && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sortedLeagues.length === 0 && (
              <tr>
                <td colSpan={canManage ? 6 : 5} className="text-gray-600">No leagues found.</td>
              </tr>
            )}
            {sortedLeagues.map((league) => (
              <tr key={league.id}>
                <td>{league.name}</td>
                <td>{orgById[league.organizationId] ?? "Unknown org"}</td>
                <td>{summarizeAdmins(league.admins)}</td>
                <td>{league.seasonId ? (seasonLabelById[league.seasonId] ?? "Unknown season") : "None"}</td>
                <td>{league.restRuleId ? (ruleNameById[league.restRuleId] ?? "Unknown rule") : "None"}</td>
                {canManage && adminScope.canManageLeague(league.id, league.organizationId) && (
                  <td>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingId(league.id);
                          setEditingName(league.name);
                          setEditingSeasonId(league.seasonId ?? "");
                          setEditingRestRuleId(league.restRuleId ?? "");
                          setEditingAdmins(league.admins?.filter(Boolean) ?? []);
                          setEditOpen(true);
                        }}
                        className="bg-yellow-500 text-white px-3 py-1 rounded"
                      >
                        Edit
                      </button>
                      <button onClick={() => handleDelete(league.id)} className="bg-red-600 text-white px-3 py-1 rounded">
                        Delete
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AppDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add League"
        footer={
          <>
            <button onClick={() => setCreateOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button onClick={handleCreate} className="bg-blue-600 text-white px-3 py-2 rounded">Save</button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            placeholder="League name"
            className="border px-3 py-2 rounded"
          />
          <select value={newOrgId} onChange={(event) => setNewOrgId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">Select organization</option>
            {visibleOrganizations.map((organization) => (
              <option key={organization.id} value={organization.id}>
              {organization.name}
              </option>
            ))}
          </select>
          <select value={newSeasonId} onChange={(event) => setNewSeasonId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No season</option>
            {seasons.map((season) => (
              <option key={season.id} value={season.id}>
                {season.name} {season.year}
              </option>
            ))}
          </select>
          <select value={newRestRuleId} onChange={(event) => setNewRestRuleId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No assigned rule</option>
            {(newOrgId ? rulesByOrg[newOrgId] ?? [] : rules.filter((rule) => rule.global)).map((rule) => (
              <option key={rule.id} value={rule.id}>{rule.name}</option>
            ))}
          </select>
        </div>
        <div className="user-role-picker">
          <div className="user-role-picker__label">Commishes</div>
          <div className="user-role-picker__grid">
            {availableCommishes.map((user) => (
              <label key={user.id} className="user-role-picker__item">
                <input
                  type="checkbox"
                  checked={newAdmins.includes(user.id)}
                  onChange={(event) =>
                    setNewAdmins((current) =>
                      event.target.checked ? [...current, user.id] : current.filter((candidate) => candidate !== user.id)
                    )
                  }
                />
                <span>{userLabelById[user.id] ?? user.id}</span>
              </label>
            ))}
          </div>
        </div>
      </AppDialog>

      <AppDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit League"
        footer={
          <>
            <button onClick={() => setEditOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button onClick={handleUpdate} className="bg-blue-600 text-white px-3 py-2 rounded">Save</button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <input
            value={editingName}
            onChange={(event) => setEditingName(event.target.value)}
            className="border px-3 py-2 rounded w-full"
            placeholder="League name"
          />
          <select value={editingSeasonId} onChange={(event) => setEditingSeasonId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No season</option>
            {seasons.map((season) => (
              <option key={season.id} value={season.id}>
                {season.name} {season.year}
              </option>
            ))}
          </select>
          <select value={editingRestRuleId} onChange={(event) => setEditingRestRuleId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No assigned rule</option>
            {(editingId
              ? rulesByOrg[leagues.find((item) => item.id === editingId)?.organizationId ?? ""] ?? []
              : []
            ).map((rule) => (
              <option key={rule.id} value={rule.id}>{rule.name}</option>
            ))}
          </select>
        </div>
        <div className="user-role-picker">
          <div className="user-role-picker__label">Commishes</div>
          <div className="user-role-picker__grid">
            {availableCommishes.map((user) => (
              <label key={user.id} className="user-role-picker__item">
                <input
                  type="checkbox"
                  checked={editingAdmins.includes(user.id)}
                  onChange={(event) =>
                    setEditingAdmins((current) =>
                      event.target.checked ? [...current, user.id] : current.filter((candidate) => candidate !== user.id)
                    )
                  }
                />
                <span>{userLabelById[user.id] ?? user.id}</span>
              </label>
            ))}
          </div>
        </div>
      </AppDialog>
    </div>
  );
};

export default LeaguesPage;
